<template>
    <cart-template
        :canBack="!isSourceSale"
        :isPlan="isPlanPath"
        :hasAlert="isSourceSale || (!isPlanPath && !isPaymentPath && isLatestPricingVersion)"
    >
        <template #configuration>
            <!-- Navigation Tabs -->
            <cart-navigation
                :smallMargin="
                    isSourceSale || (!isPlanPath && !isPaymentPath && isLatestPricingVersion)
                "
                context="renew"
            />

            <!-- EXPIRATION MESSAGE -->
            <expiration-message
                v-if="isSourceSale && !isPaymentPath"
                :expirationDate="expirationDate"
            ></expiration-message>

            <!-- Current Subscription details -->
            <subscription-summary
                context="renew"
                v-if="!isPlanPath && !isPaymentPath && isLatestPricingVersion"
            />

            <!-- Tabs Form -->
            <el-form label-position="top" :rules="setRules" :model="modelForm" ref="renewCartForm">
                <router-view store="renew" name="routerCart"></router-view>
            </el-form>
        </template>
        <!-- Summary -->
        <template #resume>
            <cart-summary context="renew">
                <template #licences>
                    <ul class="ov-auto mb-0 cart-recap-products">
                        <li
                            v-for="(product, index) in productsWithLicences"
                            :key="product.code"
                            class="flex-row"
                            :class="{ 'mb-2': index + 1 !== productsWithLicences.length }"
                        >
                            <div>
                                <p class="grey m-0 fs-3">
                                    {{
                                        $l10n.n__(
                                            '%(total)s licence %(productsName)s',
                                            '%(total)s licences %(productsName)s',
                                            products[product.id].quantity,
                                            {
                                                total: products[product.id].quantity,
                                                productsName: product.title,
                                            },
                                        )
                                    }}
                                </p>
                                <span v-if="freeLicencesByProduct(product.id)" class="grey">{{
                                    $l10n.n__(
                                        'Dont %s offerte',
                                        'Dont %s offertes',
                                        freeLicencesByProduct(product.id),
                                        freeLicencesByProduct(product.id),
                                    )
                                }}</span>
                            </div>
                            <template v-if="products[product.id].total">
                                <p
                                    v-if="freeLicencesByProduct(product.id)"
                                    class="flex-item-right my-0"
                                >
                                    <span class="strike grey semibold">
                                        {{
                                            $filters.numberFormatToFloatWithOptions(
                                                products[product.id].total.monthly.total_amount,
                                                numformat,
                                                true,
                                            )
                                        }}
                                    </span>
                                    <span class="success-50 fs-3 pl-2 semibold"
                                        >{{
                                            $filters.numberFormatToFloatWithOptions(
                                                products[product.id].total.monthly
                                                    .total_amount_discounted,
                                                numformat,
                                                true,
                                            )
                                        }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                                    >
                                </p>
                                <p v-else class="flex-item-right my-0 fs-3 semibold grey">
                                    {{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly
                                                .total_amount_discounted,
                                            numformat,
                                            true,
                                        )
                                    }}
                                    <span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>
                        <!-- Packs Summary -->
                        <li
                            v-for="(product, index) in [
                                ...packsWithLicences,
                                ...groupedPacksWithLicences,
                            ]"
                            :key="product.code"
                            class="flex-row"
                            :class="{
                                'mb-2':
                                    index + 1 !==
                                    [...packsWithLicences, ...groupedPacksWithLicences].length,
                                'mt-2': index === 0,
                            }"
                        >
                            <div>
                                <p class="grey m-0 fs-3">
                                    {{
                                        $l10n.__('Module %(productsName)s', {
                                            productsName: !!product.groups.length
                                                ? product.printableName
                                                : product.title,
                                        })
                                    }}
                                </p>
                            </div>
                            <template v-if="products[product.id].total">
                                <!-- Discount detail -->
                                <p
                                    v-if="
                                        products[product.id].total.monthly.total_amount_discounted <
                                        products[product.id].total.monthly.total_amount
                                    "
                                    class="flex-item-right my-0 semibold"
                                >
                                    <span class="strike grey semibold">
                                        {{
                                            $filters.numberFormatToFloatWithOptions(
                                                products[product.id].total.monthly.total_amount,
                                                numformat,
                                                true,
                                            )
                                        }}
                                    </span>
                                    <span class="success-50 fs-3 pl-2">
                                        <template
                                            v-if="
                                                products[product.id].total.monthly
                                                    .total_amount_discounted !== 0
                                            "
                                        >
                                            {{
                                                $filters.numberFormatToFloatWithOptions(
                                                    products[product.id].total.monthly
                                                        .total_amount_discounted,
                                                    numformat,
                                                    true,
                                                )
                                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                        </template>
                                        <template v-else>{{ $l10n.__('Offert') }}</template>
                                    </span>
                                </p>
                                <!-- Base price -->
                                <p v-else class="flex-item-right my-0 fs-3 semibold grey">
                                    {{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly
                                                .total_amount_discounted,
                                            numformat,
                                            true,
                                        )
                                    }}
                                    <span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>
                    </ul>
                </template>
                <template #subtotal>
                    <div class="flex-column fs-3 cart-recap-total-label">
                        <span class="mb-2">{{ $l10n.__('Sous-total estimé') }}</span>
                        <span class="mb-2" v-if="taxRate && taxRate !== 0"
                            >{{ $l10n.__('TVA estimée') }} {{ taxRate }}%</span
                        >
                    </div>
                    <div class="flex-column aright fs-3" v-if="isYearly">
                        <span class="semibold mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                        <span class="semibold mb-2" v-if="taxRate && taxRate !== 0">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_vat,
                                    numformat,
                                    true,
                                )
                            }}
                        </span>
                    </div>
                    <div class="flex-column aright fs-3" v-if="isMonthly">
                        <span class="semibold mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                        <span class="semibold mb-2" v-if="taxRate && taxRate !== 0">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_vat,
                                    numformat,
                                    true,
                                )
                            }}
                        </span>
                    </div>
                </template>
                <template #total>
                    <div class="flex-row jc-space" :class="{ 'mb-4': !isPaymentPath }">
                        <p class="fs-4 my-0 bold cart-recap-total-label">
                            {{ $l10n.__('Total TTC') }}
                        </p>
                        <p v-if="isYearly" class="fs-4 my-0 aright bold flex-row baseline">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_tax_included,
                                    numformat,
                                    true,
                                )
                            }}
                            <span class="fs-2">{{ $l10n.__('/an') }}</span>
                        </p>
                        <p v-if="isMonthly" class="fs-4 my-0 aright bold flex-row baseline">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_tax_included,
                                    numformat,
                                    true,
                                )
                            }}
                            <span class="fs-2">{{ $l10n.__('/mois') }}</span>
                        </p>
                    </div>
                    <div v-if="earlyRenewal" class="earlyRenewCedit mt-4">
                        <span class="separator block mb-3"></span>
                        <div class="avoir flex-row jc-space">
                            <p class="semibold fs-3 my-0 lh-20">{{ $l10n.__('Avoir estimé') }}</p>
                            <p class="semibold fs-3 my-0 lh-20">
                                -
                                {{
                                    $filters.numberFormatToFloatWithOptions(
                                        earlyRenewCredit,
                                        numformat,
                                        true,
                                    )
                                }}
                            </p>
                        </div>
                        <p class="fs-12 gray-60 mb-0 mt-1 semibold" v-if="isYearly">
                            {{
                                $l10n.__(
                                    'La prochaine échéance sera le %(nextPaymentDate)s à %(nextPaymentAmount)s',
                                    {
                                        nextPaymentDate: nextYearlyPaymentDate,
                                        nextPaymentAmount: nextYearlyPaymentAmount,
                                    },
                                )
                            }}
                        </p>
                        <p class="fs-12 gray-60 mb-0 mt-1 semibold" v-if="isMonthly">
                            {{
                                $l10n.__(
                                    'La prochaine échéance sera le %(nextPaymentDate)s à %(nextPaymentAmount)s, puis les %(dueDates)s échéances suivantes à %(dueDateAmount)s',
                                    {
                                        nextPaymentDate: nextMonthlyPaymentDate,
                                        nextPaymentAmount: nextMonthlyPaymentAmount,
                                        dueDates: numberOfDueDates,
                                        dueDateAmount: dueDatesAmount,
                                    },
                                )
                            }}
                        </p>
                    </div>
                </template>
                <template #actions>
                    <template v-if="isPaymentPath">
                        <span class="separator block my-3"></span>
                        <div class="flex-row terms mb-4">
                            <el-checkbox v-model="cguv"> </el-checkbox>
                            <span class="ml-2 fs-3" v-html="cguvLink"></span>
                        </div>
                    </template>
                    <CompassButton
                        :label="labelButton"
                        type="primary"
                        :loading="computeLoading"
                        :disabled="!cguv && isPaymentPath"
                        @click="submitForm('renewCartForm', 'nextAction')"
                        class="wp-10"
                    />
                </template>
            </cart-summary>
        </template>
    </cart-template>
</template>

<script>
import PricingV4Module from 'stores/sellsyaccountmanager/v4/renew';
import { createNamespacedHelpers } from 'vuex';
import CartTemplate from 'vcomponents/pages/account/CartTemplate.vue';
import CartSummary from 'vcomponents/pages/account/CartSummary.vue';
import FormMixin from 'mixins/FormMixins';
import AddressRules from 'vcomponents/forms/rules/AddressRules.js';
import VatIdentifierValidator from 'Services/VatIdentifierValidator';
import { checkIsTrue } from 'vcomponents/forms/rules/Rules';
import { goToUrl } from 'js/vue/helpers';
import CgvuApi from 'api/terms/cgvu.js';
import Card from 'vcomponents/Card.vue';
import { legalTermsPath, getGoSellsyUrl } from 'Config/authenticated/goSellsyPaths';
import { fetchCart } from 'api/sellsyaccountmanager';
import SubscriptionSummary from './SubscriptionSummary.vue';
import ExpirationMessage from 'vcomponents/pages/account/client/ExpirationMessage.vue';
import CartNavigation from './CartNavigation.vue';

const renewCart = createNamespacedHelpers('renewCart');
const user = createNamespacedHelpers('user');
export default {
    mixins: [FormMixin],
    data() {
        return {
            rules: {
                name: [
                    {
                        validator: (rule, value, callback, source, options) => {
                            const contact = {
                                name: this.modelForm.name,
                                forename: this.modelForm.forename,
                                email: this.modelForm.email,
                                phone: this.modelForm.phone,
                            };

                            if (_.some(contact, (info) => _.isNil(info) || !info.length))
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez renseigner des informations de contact valides',
                                        ),
                                    ),
                                );
                            callback();
                        },
                    },
                ],
                forename: [
                    { required: true, message: this.$l10n.__('Veuillez renseigner un prénom') },
                ],
                phone: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone'),
                    },
                    {
                        min: 4,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone valide'),
                    },
                ],
                email: [
                    {
                        required: true,
                        type: 'email',
                        message: this.$l10n.__('Veuillez renseigner une adresse email'),
                    },
                ],
                products: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Cart must get 2 paid licences or 1 if allowed by permission
                             */
                            if (this.customer.allowsOneLicense && this.quantityPaidLicences < 1) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 1 licence payante',
                                        ),
                                    ),
                                );
                            } else if (
                                !this.customer.allowsOneLicense &&
                                this.quantityPaidLicences < 2
                            ) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 2 licences payantes',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                        trigger: 'change',
                    },
                ],
                billingSettings: {
                    ...AddressRules,
                },
                billingLocale: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner une langue de facturation'),
                    },
                ],
                vatIntra: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Check TVA Intra format with associated country, but no require.
                             */
                            if (
                                value === '' ||
                                _.isNil(value) ||
                                _.isUndefined(this.billingSettings.countryCode)
                            )
                                return callback();

                            if (_.isNull(this.billingSettings.countryCode))
                                return callback(new Error(this.$l10n.__('Aucun pays sélectionné')));

                            let validator = new VatIdentifierValidator(
                                this.billingSettings.countryCode,
                            );
                            if (!validator.isValid(value)) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Le format du n° de tva ne correspond pas à celui du pays sélectionné',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                        trigger: 'change',
                    },
                ],
            },
            cardValidator: {
                cardNumber: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner un numéro de carte valide'),
                    },
                ],
                cardExpiry: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner une date valide'),
                    },
                ],
                cardCvc: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner un code valide'),
                    },
                ],
                cardName: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner le nom sur la carte'),
                    },
                ],
            },
        };
    },
    computed: {
        ...renewCart.mapGetters([
            'isSourceSale',
            'expirationDate',
            'modelForm',
            'previousCurrentPlanLabel',
            'currentPlanLabel',
            'recurrency',
            'products',
            'isYearly',
            'isMonthly',
            'productsWithLicences',
            'packsWithLicences',
            'groupedPacksWithLicences',
            'previousProductsWithLicences',
            'previousPacksWithLicences',
            'previousProducts',
            'previousCart',
            'computeLoading',
            'freeLicencesByProduct',
            'totalYearly',
            'totalMonthly',
            'getLocalStorage',
            'labelPlan',
            'canGoToPayment',
            'cguvValidated',
            'cguvVersion',
            'termsType',
            'cguvAlreadyValidated',
            'billingSettings',
            'isManualMethod',
            'paymentMethodLabel',
            'previousPaymentMethodLabel',
            'cartId',
            'taxRate',
            'numformat',
            'customerId',
            'context',
            'isPreviouslyMonthly',
            'isPreviouslyYearly',
            'totalProrated',
            'isBillingContactPayer',
            'isLatestPricingVersion',
            'expirationDate',
            'quantityPaidLicences',
            'earlyRenewal',
            'earlyRenewDate',
            'earlyRenewCredit',
            'customer',
        ]),
        ...user.mapState(['locale']),
        isPlanPath() {
            return this.$route.name === 'sam.v4.client.renew.form.plan';
        },
        isConfigurationPath() {
            return this.$route.name === 'sam.v4.client.renew.form.configuration';
        },
        isPaymentPath() {
            return this.$route.name === 'sam.v4.client.renew.form.payment';
        },
        isLicencesDistributionPath() {
            return this.$route.name === 'sam.v4.client.renew.form.licences';
        },
        cguv: {
            get() {
                return this.cguvValidated;
            },
            set(value) {
                return this.setCguvStatus(value);
            },
        },
        legalInformationLang() {
            const acceptedLang = ['en', 'fr'];
            const userLocale = this.locale.split('_')[0];
            return acceptedLang.includes(userLocale) ? userLocale : 'fr';
        },
        cguvLink() {
            return this.$l10n.__(
                'En cochant cette case, je reconnais avoir pris connaissance des  %(startLink)s Conditions Générales de Vente et d’Utilisation %(endLink)s de Sellsy et je les accepte.',
                {
                    startLink: `<a target='_blank' class='fs-3 semibold' href='${getGoSellsyUrl(
                        this.legalInformationLang,
                        legalTermsPath,
                    )}'>`,
                    endLink: `</a>`,
                },
            );
        },
        labelButton() {
            return this.isPaymentPath
                ? this.isManualMethod
                    ? this.$l10n.__('Valider la souscription')
                    : this.$l10n.__('Valider le paiement')
                : this.$l10n.__('Continuer');
        },
        nextRoute() {
            const actualRoute = this.$route.name;
            switch (actualRoute) {
                case 'sam.v4.client.renew.form.plan':
                    return 'sam.v4.client.renew.form.configuration';
                case 'sam.v4.client.renew.form.configuration':
                    return this.isLatestPricingVersion
                        ? 'sam.v4.client.renew.form.payment'
                        : 'sam.v4.client.renew.form.licences';
                case 'sam.v4.client.renew.form.licences':
                    return 'sam.v4.client.renew.form.payment';
                default:
                    return false;
            }
        },
        setRules() {
            if (!this.isBillingContactPayer) {
                return Object.assign({ ...this.rules }, this.cardValidator);
            } else {
                return this.rules;
            }
        },
        nextYearlyPaymentDate() {
            return this.totalYearly.upcoming_deadline
                ? moment(this.totalYearly.upcoming_deadline.date).format('ll')
                : '-';
        },
        nextYearlyPaymentAmount() {
            const amount = this.totalYearly.upcoming_deadline?.amount ?? 0;
            return this.$filters.numberFormatToFloatWithOptions(amount, this.numformat, true);
        },
        firstMonthlyPayment() {
            return this.totalMonthly.upcoming_deadlines.find((month) => month.amount > 0);
        },
        nextMonthlyPaymentDate() {
            if (!this.totalMonthly.upcoming_deadlines) {
                return '-';
            }
            return moment(this.firstMonthlyPayment.date).format('ll');
        },
        nextMonthlyPaymentAmount() {
            if (!this.totalMonthly.upcoming_deadlines) {
                return '-';
            }
            return this.$filters.numberFormatToFloatWithOptions(
                this.firstMonthlyPayment.amount,
                this.numformat,
                true,
            );
        },
        numberOfDueDates() {
            if (!this.totalMonthly.upcoming_deadlines) {
                return '-';
            }
            const months = this.totalMonthly.upcoming_deadlines.filter(
                (month) => month.amount > 0 && month.date !== this.firstMonthlyPayment.date,
            );
            return months.length;
        },
        dueDatesAmount() {
            if (!this.totalMonthly.upcoming_deadlines) {
                return '-';
            }
            const months = this.totalMonthly.upcoming_deadlines.filter(
                (month) => month.amount > 0 && month.date !== this.firstMonthlyPayment.date,
            );
            return this.$filters.numberFormatWithOptions(
                this.$filters.floatNumber(months[0].amount),
                {},
                true,
            );
        },
    },
    created() {
        this.$store.registerModule('renewCart', PricingV4Module);
        this.setSource('customer');
        this.setUserType(true);
        this.setCustomerId(this.$store.state.corp.customer);
        this.fetchMetadata({
            id: this.$store.state.corp.id,
            version: this.$store.state.corp.pricingVersion,
        });
        this.setCorporationId(this.$store.state.corp.id);
        EventBus.on('PlanCart:submitSelectedPlan', this.nextAction);
    },
    destroyed() {
        EventBus.off('PlanCart:submitSelectedPlan', this.nextAction);
    },

    methods: {
        ...renewCart.mapMutations([
            'setSource',
            'setCustomerId',
            'setProducts',
            'setCguvStatus',
            'setComputeLoading',
            'setCorporationId',
            'setCartId',
            'setUserType',
        ]),
        ...renewCart.mapActions([
            'fetchMetadata',
            'updateProductQuantity',
            'updateRecurrency',
            'updatePlan',
            'computeCart',
            'setCart',
            'setMetadata',
            'updateCart',
            'fetchCustomerData',
            'createCart',
        ]),
        nextAction() {
            if (this.nextRoute) this.continue();
            else this.createRenewCart();
        },
        continue() {
            this.$router.push({ name: this.nextRoute });
        },
        async createRenewCart() {
            try {
                await CgvuApi.acceptTerms(this.termsType, this.cguvVersion);
                if (this.$route.params.renewCartId !== 'new' || !_.isNil(this.cartId)) {
                    await this.updateCart(this.cartId);
                } else {
                    const cart = await this.createCart();
                    this.setCartId(cart.id);
                }
                if (this.isManualMethod || this.isBillingContactPayer)
                    return goToUrl(
                        this.$router.resolve({ name: 'sam.renew.payment-confirmed' }).href,
                    );
                EventBus.emit('stripe:create');
            } catch (err) {
                this.setComputeLoading(false);
                Push.error(
                    this.$l10n.__('Une erreur est survenue, veuillez ré-essayer ultérieurement.'),
                );
            }
        },
    },
    beforeDestroy() {
        this.$store.unregisterModule('renewCart');
    },
    /**
     * Get cart by UUID in params.
     * Configure cart or redirect to error before render component
     */
    async beforeRouteEnter(to, from, next) {
        if (to.params.renewCartId !== 'new') {
            await fetchCart(to.params.renewCartId, { user_context: to.meta.user_context })
                .then((response) => {
                    if (
                        response.source === 'sales' &&
                        to.name !== 'sam.v4.client.renew.form.configuration'
                    )
                        next({
                            name: 'sam.v4.client.renew.form.configuration',
                            params: { renewCartId: to.params.renewCartId },
                        });
                    next((vm) => {
                        vm.setCart(response);
                    });
                })
                .catch((err) => next(false));
        } else {
            next();
        }
    },
    components: {
        CartTemplate,
        CartSummary,
        Card,
        SubscriptionSummary,
        ExpirationMessage,
        CartNavigation,
    },
};
</script>
<style lang="scss" scoped>
.terms {
    &::v-deep {
        .el-checkbox__input {
            padding-top: 3px;
        }
    }
}
</style>
